import { Box, Typography, LinearProgress } from '@mui/material'
import { styled } from '@mui/material/styles'
import { COLORS } from '../../constants/colors'
import { TEXT } from '../../constants/text'

const VERDICTS = {
  safe:    { label: TEXT.RUG.SAFE,    color: COLORS.green.primary, max: 25 },
  caution: { label: TEXT.RUG.CAUTION, color: COLORS.gold.primary,  max: 55 },
  danger:  { label: TEXT.RUG.DANGER,  color: COLORS.red.soft,      max: 80 },
  rug:     { label: TEXT.RUG.RUG,     color: COLORS.red.primary,   max: 100 },
}

function getVerdict(score) {
  if (score <= 25) return VERDICTS.safe
  if (score <= 55) return VERDICTS.caution
  if (score <= 80) return VERDICTS.danger
  return VERDICTS.rug
}

const Wrapper = styled(Box)(() => ({
  padding: 16,
  borderRadius: 12,
  background: COLORS.bg.glass,
  border: `1px solid ${COLORS.border.light}`,
}))

const CheckRow = styled(Box)(({ passed }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 0',
  borderBottom: `1px solid ${COLORS.border.subtle}`,
  '&:last-child': { borderBottom: 'none' },
}))

/**
 * Rug Score card with score bar + individual checks.
 *
 * @param {{ score: number, checks: Record<string, boolean>, symbol: string }} props
 */
export default function RugScoreCard({ score = 0, checks = {}, symbol = '' }) {
  const verdict = getVerdict(score)

  return (
    <Wrapper>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: COLORS.text.muted }}>
          {TEXT.RUG.SCORE_LABEL} {symbol && `— ${symbol}`}
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: verdict.color }}>
          {score}
        </Typography>
      </Box>

      {/* Score bar */}
      <Box sx={{ mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={score}
          sx={{
            height: 6,
            borderRadius: 3,
            background: COLORS.bg.glass,
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              background: `linear-gradient(90deg, ${COLORS.green.primary}, ${COLORS.gold.primary}, ${COLORS.red.primary})`,
            },
          }}
        />
      </Box>

      {/* Verdict */}
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: verdict.color, mb: 1.5, textAlign: 'center' }}>
        {verdict.label}
      </Typography>

      {/* Individual checks */}
      {Object.entries(checks).length > 0 && (
        <Box>
          {Object.entries(checks).map(([key, passed]) => (
            <CheckRow key={key} passed={passed}>
              <Typography sx={{ fontSize: '0.75rem', color: COLORS.text.secondary }}>
                {TEXT.RUG.CHECKS[key.toUpperCase()] || key}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: passed ? COLORS.green.primary : COLORS.red.primary }}>
                {passed ? '✓' : '✗'}
              </Typography>
            </CheckRow>
          ))}
        </Box>
      )}
    </Wrapper>
  )
}
